import { formatDate, formatMoney, toMoney } from '@betterment/core';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, Button, Flex, HR, Modal, Text } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { trackClick } from '../../features/track';
import { PortfolioTickerDetailsModalProps } from '../data';
import { convertToPercentDisplay } from '../models';

const Table = styled.table`
  th,
  td {
    border-bottom: 0;
    padding: 0;
    text-align: right;
    vertical-align: middle;
  }

  th:first-of-type,
  td:first-of-type {
    text-align: left;
  }
`;

const PortfolioTickerDetailsModal: React.FC<React.PropsWithChildren<PortfolioTickerDetailsModalProps>> = ({
  closeHandler,
  security,
}) => {
  const {
    category = '',
    name = '',
    symbol = '',
    expenseAmount = toMoney(0),
    expensePercent = 0,
    historicalPerformances = [],
    inceptionDate = '',
    prospectusUrl = '',
  } = security || {};

  return (
    <Modal closeHandler={() => closeHandler()} contentWidth={['100%', '600px']} aria-label="Portfolio ticker details">
      <Modal.Slot name="content">
        <Box p="8">
          <Text fontSize={['3', '4']} fontWeight="medium">
            {name}
          </Text>
          <Text as="p">
            <SambaAutoLink
              onClick={() => trackClick({ name: 'TickerProspectusURL', flow: 'ExternallyAdvisedPlanFlow' })}
              to={prospectusUrl}
              target="_blank"
            >
              {symbol}
            </SambaAutoLink>
          </Text>

          <HR my="5" />

          <Box>
            <Text as="p" fontWeight="medium" mb="0">
              General info
            </Text>
            <Flex justifyContent="space-between" mt="1">
              <Flex.Item>
                <Text as="p">Gross expense ratio (%)</Text>
              </Flex.Item>
              <Flex.Item>
                <Text as="p">{convertToPercentDisplay(expensePercent)}</Text>
              </Flex.Item>
            </Flex>

            <Flex justifyContent="space-between" mt="1">
              <Flex.Item>
                <Text as="p">Gross expense ratio (per $1,000)</Text>
              </Flex.Item>
              <Flex.Item>
                <Text as="p">{formatMoney(expenseAmount)}</Text>
              </Flex.Item>
            </Flex>

            <Flex justifyContent="space-between" mt="1">
              <Flex.Item>
                <Text as="p">Category</Text>
              </Flex.Item>
              <Flex.Item>
                <Text as="p">{category}</Text>
              </Flex.Item>
            </Flex>
          </Box>

          <HR my="5" />

          <Box width="100%">
            <Table role="table">
              <thead>
                <tr>
                  <th>
                    <Text as="p">Historical performance</Text>
                  </th>
                  <th>
                    <Text as="p" fontWeight="normal">
                      <SambaAutoLink
                        onClick={() => trackClick({ name: 'TickerProspectusURL', flow: 'ExternallyAdvisedPlanFlow' })}
                        to={prospectusUrl}
                        target="_blank"
                      >
                        {symbol}
                      </SambaAutoLink>
                    </Text>
                  </th>
                  <th>
                    <Text as="p" fontWeight="normal" ml="2">
                      Benchmark
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {historicalPerformances.map(item => (
                  <tr key={item.timePeriod}>
                    {item.timePeriod === 'Inception' ? (
                      <td>
                        <Text as="p" mb="0" mt="1">
                          {item.timePeriod}
                        </Text>
                        <Text as="p" color="bmtGrey50" fontSize="1">
                          {formatDate(inceptionDate, 'longOrdinalSlashed')}
                        </Text>
                      </td>
                    ) : (
                      <td>
                        <Text as="p" mt="1">
                          {item.timePeriod}
                        </Text>
                      </td>
                    )}
                    <td>
                      <Text as="p" mt="1">
                        {convertToPercentDisplay(item.percent)}
                      </Text>
                    </td>
                    <td>
                      <Text as="p" mt="1">
                        {convertToPercentDisplay(item.benchmarkPercent)}
                      </Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>

          <HR my="5" />

          <Box>
            <Text as="p" color="bmtGrey50" mb="0">
              The Morningstar benchmark ({category}) is appropriate for comparing historical performance over the same
              time periods.
            </Text>
          </Box>

          <Button className="ft-modalClose sc-m-t--m" onClick={() => closeHandler()} width={['100%', 'fit-content']}>
            Close
          </Button>
        </Box>
      </Modal.Slot>
    </Modal>
  );
};

export default PortfolioTickerDetailsModal;
